import * as React from "react";

import type { DuprHomeStatusDto, DuprProfileDto } from "@volley/data";

import fetchApi, { logFetchError } from "../../util/fetchApi";
import { useSelectedSport } from "../common/context/sport";

export interface HomeDuprValue {
    /** The linked official DUPR profile, or null when not linked. */
    profile: DuprProfileDto | null;
    /** Whether this player has ever pushed an assessment rating to DUPR. */
    hasSubmitted: boolean;
    /**
     * True while the answer could still change. Consumers must withhold DUPR UI
     * rather than render a default — showing "Connect DUPR" or the wrong
     * assessment copy and then correcting it is the flash this exists to prevent.
     */
    loading: boolean;
    /**
     * Re-read after something changes the answer — linking an account, or
     * submitting a rating. `DuprConnect` links inside its own dialog without
     * telling its host, so the host refetches when the dialog closes.
     */
    refetch: () => Promise<void>;
}

const HomeDuprContext = React.createContext<HomeDuprValue | null>(null);

/**
 * The home screen's DUPR state, fetched once.
 *
 * Before this, home made three DUPR requests per load: the profile twice (two
 * sibling components with no shared state) plus an SSO config call nothing on the
 * screen read. It also had no access to "has this player submitted a rating",
 * which lives on the predicted-ratings summary whose query is far too heavy to
 * run for one boolean. `GET /api/integrations/dupr/home-status` answers all of it
 * in one cheap request.
 */
export function HomeDuprProvider({
    children,
}: {
    children: React.ReactNode;
}): React.JSX.Element {
    const { selected: sport } = useSelectedSport();

    // Only pickleball players have any use for this, so nobody else pays for the
    // request. This used to also wait on the DUPR flow feature, which meant
    // waiting on `featuresLoaded` to tell "not granted" apart from "not loaded
    // yet" — the assessment is no longer feature-gated, so the sport is the
    // whole condition.
    const shouldFetch = sport === "PICKLEBALL";

    const [profile, setProfile] = React.useState<DuprProfileDto | null>(null);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    // Starts true so consumers withhold rather than flash: the sport context
    // reports PLATFORM_TENNIS before it resolves, so "not pickleball" is not yet
    // a settled answer on the first render.
    const [loading, setLoading] = React.useState(true);

    // A ref, not an effect-scoped flag: `load` is also called manually via
    // refetch(), whose result can land after unmount just as the initial fetch's
    // can, and a per-effect closure wouldn't cover that.
    const mounted = React.useRef(true);
    React.useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const load = React.useCallback(async (): Promise<void> => {
        if (!shouldFetch) {
            // Settled, not pending: there is nothing to wait for.
            if (mounted.current) {
                setProfile(null);
                setHasSubmitted(false);
                setLoading(false);
            }
            return;
        }
        setLoading(true);
        try {
            const res = await fetchApi<DuprHomeStatusDto>(
                "/api/integrations/dupr/home-status",
            );
            if (mounted.current) {
                setProfile(res.profile);
                setHasSubmitted(res.hasSubmittedToDupr);
            }
        } catch (err) {
            // No error state: DUPR standing is supplementary on this screen, so a
            // failure degrades to the unlinked presentation rather than
            // interrupting the home screen.
            logFetchError(err, "Failed to load DUPR home status");
            if (mounted.current) {
                setProfile(null);
                setHasSubmitted(false);
            }
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, [shouldFetch]);

    React.useEffect(() => {
        void load();
    }, [load]);

    const value = React.useMemo(
        () => ({ profile, hasSubmitted, loading, refetch: load }),
        [profile, hasSubmitted, loading, load],
    );

    return (
        <HomeDuprContext.Provider value={value}>
            {children}
        </HomeDuprContext.Provider>
    );
}

/** The home screen's shared DUPR state. Must be used under HomeDuprProvider. */
export function useHomeDupr(): HomeDuprValue {
    const context = React.useContext(HomeDuprContext);
    if (!context) {
        throw new Error("useHomeDupr must be used within HomeDuprProvider");
    }
    return context;
}
